import type { Vec2, Ring, PolygonWithHoles } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Gate } from '../../../data/types';
import type { MeshBuilder, VertexStyle, Frame } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { layoutToWorld } from '../../../core/coords';
import { difference } from '../../../geo/clip';
import { boxSolid, coneSolid, frustumSolid, horizontalCap } from '../solids';
import { gableRoof } from '../roofSolids';
import { LANDMARK_PATTERN, patterned } from '../landmarks/surfacePatterns';
import { SINT_ANTONIESPOORT, SINT_ANTONIESPOORT_ID } from '../landmarks/fortress/sintAntoniespoort';
import { REGULIERSPOORT } from '../landmarks/fortress/regulierspoort';
import { REGULIERSPOORT_ID } from '../landmarks/fortress/regulierspoortPlan';
import { JAN_ROODENPOORT } from '../landmarks/fortress/janRoodenpoort';
import { JAN_ROODENPOORT_ID } from '../landmarks/fortress/janRoodenpoortPlan';
import { FORTIFICATION_ROOF } from './roofs';
import type { BespokeGate, GateBlock, GatePassage } from './bespokeGate';
import { buildHaarlemmerpoort } from './haarlemmerpoort';
import { buildHeiligewegspoort } from './heiligewegspoort';
import { BLOKHUIS_GATE_ID, blokhuisPassageWidth, buildBlokhuisGate } from './blokhuisGate';

export type { GateBlock, GatePassage } from './bespokeGate';

export interface GateBuildResult {
  readonly triangles: number;
  /** The gatehouse less its passage, in as many pieces as that leaves. */
  readonly obstacles: readonly PolygonWithHoles[];
}

interface GateStyles {
  readonly wall: VertexStyle;
  readonly roof: VertexStyle;
}

export interface GateColours {
  readonly wall: LinearRGB;
  /** The tiles of a gate on the layout's box. */
  readonly roof: LinearRGB;
  /** The brick and slate of a gate on its own plan, as painted, and the dark of its openings. */
  readonly planWall: LinearRGB;
  readonly slate: LinearRGB;
  readonly dark: LinearRGB;
  /** The pale stone of a bespoke gate's mouldings: the painting's highlight white. */
  readonly trim: LinearRGB;
  /** Timber of the temporary Lastage entrance, rather than domestic stonework. */
  readonly timber?: LinearRGB;
}

/* Gates whose plan of 1538 is known are built on it; the layout's record places and names them. */
const BESPOKE: ReadonlyMap<string, BespokeGate> = new Map([
  [SINT_ANTONIESPOORT_ID, SINT_ANTONIESPOORT],
  [REGULIERSPOORT_ID, REGULIERSPOORT],
  [JAN_ROODENPOORT_ID, JAN_ROODENPOORT],
]);

const SPEC = FORTIFICATION_ROOF.gate;

function passageWidth(gate: Gate): number {
  return gate.id === BLOKHUIS_GATE_ID
    ? blokhuisPassageWidth(gate)
    : Math.min(SPEC.maxPassageWidth, gate.width * SPEC.passageWidthRatio);
}

function roundTower(
  builder: MeshBuilder,
  styles: GateStyles,
  frame: Frame,
  center: Vec2,
  radius: number,
  height: number
): void {
  const spec = { center, radius, segments: SPEC.flankingTower.segments, phase: 0 };
  frustumSolid(builder, styles.wall, frame, spec, 0, height, radius, { top: false, bottom: false });
  const rise = radius * FORTIFICATION_ROOF.tower.cone.riseRatio;
  coneSolid(builder, styles.roof, frame, spec, height, height + rise, false);
}

/** Plan u runs along the gate heading (through the passage), v across it. */
function buildLandGate(builder: MeshBuilder, styles: GateStyles, frame: Frame, gate: Gate): void {
  const hw = gate.width / 2;
  const hd = gate.depth / 2;
  const hp = passageWidth(gate) / 2;
  const closed = { top: true, bottom: false };
  boxSolid(builder, styles.wall, frame, [-hd, 0, hp], [hd, gate.height, hw], closed);
  boxSolid(builder, styles.wall, frame, [-hd, 0, -hw], [hd, gate.height, -hp], closed);
  boxSolid(builder, styles.wall, frame, [-hd, SPEC.passageHeight, -hp], [hd, gate.height, hp], {
    top: true,
    bottom: true,
  });
  const radius = gate.width * SPEC.flankingTower.radiusRatio;
  const towerHeight = gate.height * SPEC.flankingTower.heightRatio;
  for (const side of [1, -1])
    roundTower(builder, styles, frame, [-hd, side * hw], radius, towerHeight);
  const rise = Math.min(gate.width, gate.depth) * SPEC.hipRiseRatio;
  const eave = gate.height;
  gableRoof(builder, styles, frame, {
    u0: -hd,
    u1: hd,
    v0: -hw,
    v1: hw,
    eaveY: eave,
    ridgeY: eave + rise,
    ends: { start: true, end: true },
  });
  horizontalCap(
    builder,
    styles.wall,
    frame,
    [
      [-hd, -hw],
      [hd, -hw],
      [hd, hw],
      [-hd, hw],
    ],
    eave,
    false
  );
}

function buildWaterGate(builder: MeshBuilder, styles: GateStyles, frame: Frame, gate: Gate): void {
  const radius = gate.width * SPEC.waterTower.radiusRatio;
  for (const side of [1, -1]) {
    roundTower(builder, styles, frame, [0, side * (gate.width / 2 + radius)], radius, gate.height);
  }
  const { height, depth, thickness } = SPEC.waterArch;
  const hw = gate.width / 2;
  boxSolid(
    builder,
    styles.wall,
    frame,
    [-depth / 2, height - thickness / 2, -hw],
    [depth / 2, height + thickness / 2, hw],
    { top: true, bottom: true }
  );
}

/* The walls a gate joins are exaggerated in the painter view, so the whole gatehouse is too, or
   the walls would rise past its roof; it moves as one by the warp at the foot of its centre. */
function baseStyle(anchor: Vec2, edgeId: number, seed: number) {
  return { edgeId, seed, strokeAngle: 0, exaggeration: { base: 0, anchor } };
}

/* De Graauw's late Gothic brick, measured on this gate, and the painting's grey slate. */
function buildBespoke(
  builder: MeshBuilder,
  bespoke: BespokeGate,
  colours: GateColours,
  base: ReturnType<typeof baseStyle>
): void {
  const wall: VertexStyle = {
    ...base,
    color: colours.planWall,
    surface: SURFACE.stone,
    extent: [22.5, 9.6],
  };
  bespoke.build(builder, {
    wall: patterned(wall, LANDMARK_PATTERN.lateGothic),
    roof: patterned(
      { ...base, color: colours.slate, surface: SURFACE.roofSlate, extent: [15.9, 10.1] },
      LANDMARK_PATTERN.slate
    ),
    dark: { ...wall, color: colours.dark, extent: [1, 1] },
    trim: { ...wall, color: colours.trim, extent: [1, 1] },
  });
}

export function buildGate(
  builder: MeshBuilder,
  gate: Gate,
  colours: GateColours,
  edgeId: number,
  seed: number
): GateBuildResult {
  const before = builder.triangleCount;
  const bespoke = BESPOKE.get(gate.id);
  if (bespoke) {
    buildBespoke(builder, bespoke, colours, baseStyle(bespoke.anchor, edgeId, seed));
    return { triangles: builder.triangleCount - before, obstacles: bespoke.obstacles() };
  }
  const frame: Frame = { origin: layoutToWorld(gate.pos, 0), yaw: gate.heading };
  const base = baseStyle(gate.pos, edgeId, seed);
  const styles: GateStyles = {
    wall: {
      ...base,
      color: colours.wall,
      surface: SURFACE.stone,
      extent: [gate.width, gate.height],
    },
    roof: { ...base, color: colours.roof, surface: SURFACE.roofTile, extent: [gate.width, 5] },
  };
  if (gate.id === BLOKHUIS_GATE_ID)
    buildBlokhuisGate(builder, frame, gate, {
      ...styles,
      timber: { ...styles.wall, color: colours.timber ?? colours.wall, surface: SURFACE.wood },
      dark: { ...styles.wall, color: colours.dark },
    });
  else if (gate.id === 'gate-haarlemmerpoort')
    buildHaarlemmerpoort(
      builder,
      {
        ...styles,
        slate: { ...styles.roof, color: colours.slate, surface: SURFACE.roofSlate },
        dark: { ...styles.wall, color: colours.dark },
      },
      frame,
      gate
    );
  else if (gate.id === 'gate-heiligewegspoort')
    buildHeiligewegspoort(builder, frame, gate, {
      ...styles,
      roof: { ...styles.roof, color: colours.slate, surface: SURFACE.roofSlate },
      dark: { ...styles.wall, color: colours.dark },
    });
  else if (gate.kind === 'land') buildLandGate(builder, styles, frame, gate);
  else buildWaterGate(builder, styles, frame, gate);
  return { triangles: builder.triangleCount - before, obstacles: gateObstacles(gate) };
}

function rotateAndTranslate(u: number, v: number, cos: number, sin: number, center: Vec2): Vec2 {
  return [center[0] + u * cos - v * sin, center[1] + u * sin + v * cos];
}

/** The way through a land gate; a water gate has none. */
export function gatePassage(gate: Gate): GatePassage | null {
  const bespoke = BESPOKE.get(gate.id);
  if (bespoke) return bespoke.passage();
  if (gate.kind === 'water') return null;
  const cos = Math.cos(gate.heading);
  const sin = Math.sin(gate.heading);
  const hd = gate.depth / 2;
  return {
    from: rotateAndTranslate(-hd, 0, cos, sin, gate.pos),
    to: rotateAndTranslate(hd, 0, cos, sin, gate.pos),
    halfWidth: passageWidth(gate) / 2,
  };
}

function gateRect(gate: Gate, hu: number, hv: number): Ring {
  const cos = Math.cos(gate.heading);
  const sin = Math.sin(gate.heading);
  return [
    rotateAndTranslate(-hu, -hv, cos, sin, gate.pos),
    rotateAndTranslate(hu, -hv, cos, sin, gate.pos),
    rotateAndTranslate(hu, hv, cos, sin, gate.pos),
    rotateAndTranslate(-hu, hv, cos, sin, gate.pos),
  ];
}

/** The whole plan of a gatehouse, its passage included: no lot reaches into it (reserved.ts). */
export function gateFootprint(gate: Gate): PolygonWithHoles {
  const bespoke = BESPOKE.get(gate.id);
  if (bespoke) return bespoke.footprint();
  return { outer: gateRect(gate, gate.depth / 2, gate.width / 2), holes: [] };
}

/** The gatehouse less its passage (PLAN, Obstacles): a land gate's masonry either side of it. */
export function gateObstacles(gate: Gate): PolygonWithHoles[] {
  const bespoke = BESPOKE.get(gate.id);
  if (bespoke) return bespoke.obstacles();
  const full = gateFootprint(gate);
  if (gate.kind === 'water') return [full];
  const passage = gateRect(gate, gate.depth / 2 + 0.1, passageWidth(gate) / 2);
  return [...difference([full], [{ outer: passage, holes: [] }])];
}

/** Whether the gate is built on its own plan of 1538 rather than on its layout box. */
export function isBespokeGate(gate: Gate): boolean {
  return BESPOKE.has(gate.id);
}

/** The block the town walls run into at a land gate: the gatehouse, or the part of it they meet. */
export function gateBlock(gate: Gate): GateBlock {
  const bespoke = BESPOKE.get(gate.id);
  if (bespoke) return bespoke.block();
  return { centre: gate.pos, heading: gate.heading, half: [gate.depth / 2, gate.width / 2] };
}
